import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { movieInfo, movieStillImages, recommendedMovies } from "@/mocks/movies";

const TOTAL_SECONDS = 158 * 60;

function formatTime(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export default function Watch() {
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const progress = (seconds / TOTAL_SECONDS) * 100;

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setSeconds((s) => {
        const next = s + TOTAL_SECONDS / 240;
        if (next >= TOTAL_SECONDS) {
          setPlaying(false);
          setEnded(true);
          return TOTAL_SECONDS;
        }
        return next;
      });
    }, 500);
    return () => clearInterval(timer);
  }, [playing]);

  const start = () => {
    setEnded(false);
    setPlaying(true);
    if (seconds >= TOTAL_SECONDS) setSeconds(0);
  };

  const togglePlay = () => {
    if (ended) {
      setSeconds(0);
      setEnded(false);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };

  const seek = (clientX: number) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setSeconds(ratio * TOTAL_SECONDS);
    setEnded(false);
  };

  const recap = useMemo(() => movieInfo.summary, []);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-4">
          <Link to="/" className="hover:text-accent-500 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">在线播放</span>
        </nav>

        <div className="rounded-lg overflow-hidden bg-background-50">
          <div className="relative w-full aspect-video bg-background-50">
            <img
              src={movieStillImages[3]}
              alt="《色戒》播放画面演示"
              title="色戒 在线观看"
              className={`w-full h-full object-cover object-top transition-opacity duration-500 ${
                playing ? "opacity-35" : "opacity-60"
              }`}
            />
            <div className="absolute inset-0 bg-background-50/40" />

            {!playing && !ended && (
              <button
                onClick={start}
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 cursor-pointer group"
                aria-label="开始播放"
              >
                <span className="w-16 h-16 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                  <i className="ri-play-fill text-3xl md:text-5xl" />
                </span>
                <span className="px-3 py-1 rounded-full bg-background-50/15 border border-background-50/25 text-xs text-foreground-950 backdrop-blur-sm">
                  <i className="ri-information-line mr-1" />
                  本站为播放演示，无真实视频源
                </span>
              </button>
            )}

            {ended && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background-50/70 animate-fade-in">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500 text-primary-950">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </span>
                <p className="font-heading text-lg text-foreground-950">播放结束（演示）</p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={togglePlay}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-foreground-950 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-restart-line" />
                    重新播放
                  </button>
                  <Link
                    to="/reviews"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-100 border border-background-300 text-foreground-950 text-sm hover:border-accent-500 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-book-open-line" />
                    去看影评
                  </Link>
                </div>
              </div>
            )}

            {playing && (
              <span className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 rounded-md bg-background-50/15 border border-background-50/25 text-xs text-foreground-950 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
                正在播放 · 演示模式
              </span>
            )}

            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-background-50/95 to-transparent px-4 md:px-6 pt-10 pb-4">
              <div
                ref={barRef}
                onClick={(e) => seek(e.clientX)}
                className="group w-full h-1.5 rounded-full bg-background-50/25 cursor-pointer relative"
              >
                <div
                  className="h-full rounded-full bg-accent-500 relative"
                  style={{ width: `${progress}%` }}
                >
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-accent-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={togglePlay}
                    aria-label={playing ? "暂停" : "播放"}
                    className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-950 hover:text-accent-500 transition-colors cursor-pointer"
                  >
                    <i className={playing ? "ri-pause-fill text-xl" : "ri-play-fill text-xl"} />
                  </button>
                  <button
                    onClick={() => setMuted((m) => !m)}
                    aria-label="音量"
                    className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-950 hover:text-accent-500 transition-colors cursor-pointer"
                  >
                    <i className={muted ? "ri-volume-mute-line text-lg" : "ri-volume-up-line text-lg"} />
                  </button>
                  <span className="text-xs text-foreground-700 tabular-nums">
                    {formatTime(seconds)} <span className="text-secondary-600">/ {formatTime(TOTAL_SECONDS)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline text-xs text-secondary-500">高清 1080P</span>
                  <button
                    aria-label="全屏"
                    className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-950 hover:text-accent-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-fullscreen-line text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="px-3 py-1 rounded-full bg-primary-500/15 text-accent-500 text-xs">
            <i className="ri-information-line mr-1" />
            演示播放，无真实视频资源
          </span>
          <span className="px-3 py-1 rounded-full bg-background-100 border border-background-200 text-secondary-500 text-xs">
            视频资源暂时不可用，敬请期待
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {movieInfo.title}
              <span className="ml-3 align-middle text-base font-normal text-secondary-500">{movieInfo.englishTitle}</span>
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-500">
              <span className="inline-flex items-center gap-1 text-accent-500 font-medium">
                <i className="ri-star-fill text-xs" />
                {movieInfo.rating}
              </span>
              <span>{movieInfo.year}</span>
              <span>{movieInfo.region}</span>
              <span>{movieInfo.duration}</span>
              <span>{movieInfo.genre}</span>
            </div>

            <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情回顾</h2>
              <p className="mt-3 text-sm md:text-[15px] leading-loose text-foreground-700">{recap}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {movieStillImages.slice(0, 2).map((img, i) => (
                  <div key={img} className="w-full aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                    <img
                      src={img}
                      alt={`《色戒》剧照 ${i + 1}`}
                      title="色戒 剧照"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">影片信息</h3>
              <dl className="mt-4 divide-y divide-background-200 text-sm">
                <div className="flex justify-between gap-4 py-3">
                  <dt className="text-secondary-500">导演</dt>
                  <dd className="text-foreground-900">{movieInfo.director}</dd>
                </div>
                <div className="flex justify-between gap-4 py-3">
                  <dt className="text-secondary-500">主演</dt>
                  <dd className="text-foreground-900 text-right">{movieInfo.cast}</dd>
                </div>
                <div className="flex justify-between gap-4 py-3">
                  <dt className="text-secondary-500">上映年份</dt>
                  <dd className="text-foreground-900">{movieInfo.year}</dd>
                </div>
                <div className="flex justify-between gap-4 py-3">
                  <dt className="text-secondary-500">片长</dt>
                  <dd className="text-foreground-900">{movieInfo.duration}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg bg-primary-900 text-foreground-950 p-5">
              <h3 className="font-heading text-base font-bold">深入了解剧情</h3>
              <p className="mt-2 text-xs text-primary-100/80 leading-relaxed">
                想了解完整的故事脉络与制作背景？前往剧情介绍页。
              </p>
              <Link
                to="/story"
                className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-book-open-line" />
                查看剧情介绍
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-14 md:mt-20">
          <SectionHeading title="相关推荐" subtitle="喜欢这部影片，也许你还会喜欢（演示示例）" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {recommendedMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}